import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Avatar } from "./Members";

export const EditProfileDialog = ({ open, onOpenChange, profile, onSaved, title = "Edit profile", intro }) => {
  const { refreshUser } = useApp();
  const [f, setF] = useState({ nickname: "", bio: "", links: [] });
  const [avatar, setAvatar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && profile) {
      setF({ nickname: profile.nickname || "", bio: profile.bio || "", links: profile.links?.length ? profile.links : [] });
      setAvatar(profile.avatar_url);
    }
  }, [open, profile]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be 5 MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { const r = await api.post("/profile/avatar", fd); setAvatar(r.data.avatar_url); refreshUser(); toast.success("Profile image updated"); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const setLink = (i, k, v) => setF({ ...f, links: f.links.map((l, j) => (j === i ? { ...l, [k]: v } : l)) });
  const save = async () => {
    setSaving(true);
    try {
      const links = f.links.filter((l) => l.label.trim() || l.url.trim()).map((l) => ({ label: (l.label.trim() || l.url.replace(/^https?:\/\//, "").split("/")[0]).slice(0, 40), url: /^https?:\/\//.test(l.url.trim()) ? l.url.trim() : `https://${l.url.trim()}` }));
      const r = await api.put("/profile", { ...f, links });
      toast.dismiss("hive-signin");
      toast.success("Profile saved"); refreshUser(); onSaved?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161F] border-lime/20 max-w-lg text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-wide" data-testid="edit-profile-title">{title}</DialogTitle></DialogHeader>
        {intro && <p className="text-xs text-slate-400 -mt-2">{intro}</p>}
        <div className="flex items-center gap-4">
          <Avatar url={avatar} address={profile?.address} size={72} />
          <div className="space-y-1.5">
            <button data-testid="profile-avatar-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 rounded-lg border border-lime/40 px-3 py-1.5 text-xs text-lime hover:bg-lime/10">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload image
            </button>
            <p className="text-[10px] text-slate-500">PNG, JPG, GIF or WebP · max 5 MB</p>
            <input ref={fileRef} data-testid="profile-avatar-file-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </div>
        </div>
        <label className="block text-xs text-slate-400">Nickname *
          <input data-testid="profile-nickname-input" value={f.nickname} maxLength={24} onChange={(e) => setF({ ...f, nickname: e.target.value })} className="field mt-1" /></label>
        <label className="block text-xs text-slate-400">Bio <span className="text-slate-600">({f.bio.length}/500)</span>
          <textarea data-testid="profile-bio-input" rows={3} maxLength={500} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="field mt-1" /></label>
        <div className="space-y-2">
          <p className="text-xs text-slate-400">Links</p>
          {f.links.map((l, i) => (
            <div key={i} className="flex gap-2" data-testid={`profile-link-row-${i}`}>
              <input data-testid={`profile-link-label-${i}`} value={l.label} maxLength={40} onChange={(e) => setLink(i, "label", e.target.value)} placeholder="Label" className="field py-2 text-xs w-1/3" />
              <input data-testid={`profile-link-url-${i}`} value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="https://…" className="field py-2 text-xs flex-1" />
              <button data-testid={`profile-link-remove-${i}`} onClick={() => setF({ ...f, links: f.links.filter((_, j) => j !== i) })} className="px-2 text-slate-500 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button data-testid="profile-add-link-button" onClick={() => setF({ ...f, links: [...f.links, { label: "", url: "" }] })} className="flex items-center gap-1 text-xs text-lime hover:underline"><Plus className="h-3.5 w-3.5" /> Add link</button>
        </div>
        <p className="text-[10px] text-slate-600">Nicknames are unique. Reserved words (admin, official, robinhood…) are blocked to prevent impersonation.</p>
        <button data-testid="profile-save-button" onClick={save} disabled={saving} className="btn-lime w-full py-2.5 text-sm">{saving ? "Saving…" : "Save profile"}</button>
      </DialogContent>
    </Dialog>
  );
};
